'use client'

import { useEffect } from 'react'
import { cn } from '@/lib/utils'
import { getTheme, setTheme, THEME_KEY, isTheme, applyTheme } from '@/lib/theme'

/**
 * Light/dark switch. Both glyphs are always in the markup; which one shows is
 * decided by the inherited `--rf-show-light` / `--rf-show-dark` vars from
 * app/globals.css. That keeps the server render theme-agnostic (no hydration
 * mismatch, no icon flip after load) and means the button is correct even
 * before React hydrates.
 */
export default function ThemeToggle({ className }: { className?: string }) {
  // Keep tabs in sync: a change in one window updates the others.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === THEME_KEY && isTheme(e.newValue)) applyTheme(e.newValue)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return (
    <button
      type="button"
      onClick={() => setTheme(getTheme() === 'dark' ? 'light' : 'dark')}
      className={cn(
        'flex h-10 w-10 items-center justify-center rounded-md text-fg-secondary transition-colors duration-200 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fg focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        className
      )}
    >
      {/* Shown in dark mode: offers the switch to light. */}
      <span style={{ display: 'var(--rf-show-dark)' }} className="items-center">
        <span className="sr-only">Switch to light theme</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.75" />
          <path
            d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
          />
        </svg>
      </span>
      {/* Shown in light mode: offers the switch to dark. */}
      <span style={{ display: 'var(--rf-show-light)' }} className="items-center">
        <span className="sr-only">Switch to dark theme</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11Z"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  )
}
